import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { Metrics, Colors } from '../../themes';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import clsx from 'clsx';
import Select from './Select';
import SearchField from './SearchField';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';
import { hackToSubtractOffset } from '../../lib/Utils';
import AutorenewIcon from '@material-ui/icons/Autorenew';
// include all the lines below for date picker
import DatePicker from 'react-datepicker';
import i18n from '../../i18n';
import '../common/styles/react-datepicker.custom.css';
import { registerLocale } from 'react-datepicker';
import fi from 'date-fns/locale/fi';
import pl from 'date-fns/locale/pl';
import de from 'date-fns/locale/de';
import sv from 'date-fns/locale/sv';
import es from 'date-fns/locale/es';
registerLocale('fi', fi);
registerLocale('pl', pl);
registerLocale('de', de);
registerLocale('sv', sv);
registerLocale('es', es);
// end of date picker

const useStyles = makeStyles((theme) => ({
  firstPartContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flex: 2,
  },
  noFlex: {
    flex: 0,
  },
  selectAllHeaderContainer: {
    flex: 1,
    flexDirection: 'column',
  },
  tableHeader: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingLeft: 10,
  },
  extraPadding: {
    paddingLeft: 20,
  },
  twoRowHeaderContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    paddingRight: 20,
    marginBottom: 20,
  },
  emptyColumn: {
    flex: 1,
  },
  putSecond: {
    order: 2,
  },
  pageAndSearchContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: 2,
    maxWidth: 760,
  },
  executeButtonContainer: {
    display: 'flex',
    flex: 1,
    alignSelf: 'flex-end',
  },
  buttonNextToShowPerPage: {
    margin: 0,
    alignSelf: 'flex-end',
  },
  button: {
    margin: theme.spacing(2),
    fontSize: 14,
    color: Colors.white,
    border: `1px solid ${Colors.disabledGrey}`,
    '&:hover': {
      background: Colors.disabledGrey,
    },
    '&:disabled': {
      border: `1px solid ${Colors.disabledGrey}`,
      background: Colors.navBackgroundGrey,
      '&:hover': {
        background: Colors.black,
      },
    },
  },
  smallSizeButton: {
    height: 35,
    fontSize: 12,
    paddingLeft: 6,
    paddingRight: 6,
  },
  executeButton: {
    marginLeft: 'auto',
    marginRight: 0,
  },
  includeDeletedSwitch: {
    marginRight: 20,
  },
  column: {
    flexDirection: 'column',
    flexWrap: 'wrap-reverse',
  },
  monitoringHeader: {
    paddingRight: 20,
  },
  sitesButton: {
    flex: 1,
    maxWidth: 300,
    fontWeight: 600,
    fontSize: 16,
    margin: '10px 10px 10px 0',
    color: Colors.white,
    border: `1px solid ${Colors.disabledGrey}`,
    '&:hover': {
      background: Colors.disabledGrey,
    },
  },
  headerMiddleButtonContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxHeight: 39,
    marginTop: 17,
    margin: theme.spacing(2),
    fontSize: 14,
    color: Colors.white,
    border: `1px solid ${Colors.disabledGrey}`,
    '&:hover': {
      background: Colors.disabledGrey,
    },
    '&:disabled': {
      border: `1px solid ${Colors.disabledGrey}`,
      background: Colors.navBackgroundGrey,
      '&:hover': {
        background: Colors.black,
      },
    },
  },
  checkAll: {
    textAlign: 'left',
    marginLeft: -10,
  },
  checkAllLabel: {
    marginLeft: 20,
    fontSize: 16,
  },
}));

const TableHeader = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    hasAjax,
    isAuditLogsHeader,
    isMonitoring,
    includeAjax,
    onChangeIncludeAjax,
    includeDeletedController,
    onChangeIncludeDeletedController,
    headerMiddleButtonText,
    headerMiddleButtonIcon,
    headerMiddleButtonAction,
    isThreeElementsHeader,
    isSelectAll,
    isSelectAllControllersButtonChecked,
    onAdd2ButtonClick,
    add2ButtonText,
  } = props;
  const { width } = props.size;
  const displayColumn = width < (isThreeElementsHeader ? Metrics.tableWidth.small : Metrics.tableWidth.tiny);
  const smallerThanSmall = useMediaQuery(`(max-width: ${Metrics.breakpoints.sm}px)`);
  // additions for audit logs
  const isOneColumn = width < Metrics.auditHeaderColumns.two;
  const isTwoColumns = width < Metrics.auditHeaderColumns.three;
  const isBetween = width < Metrics.auditHeaderColumns.between;
  const isThreeColumns = width >= Metrics.auditHeaderColumns.three;
  const sizeFactor =
    onChangeIncludeDeletedController || headerMiddleButtonAction ? 0.25 : isAuditLogsHeader && isTwoColumns ? 0.3 : 0.4;
  const maxSearchWidth = 480;
  const searchWidth =
    !isAuditLogsHeader && (smallerThanSmall || isThreeElementsHeader)
      ? '100%'
      : Math.min(maxSearchWidth, width * sizeFactor); // for some reason the TextField had a maxWidth of 225 and didn't scale above that. This hack solves this
  const fullWidth = width - 40;
  const formInputWidth = isOneColumn ? fullWidth : isTwoColumns ? (width - 80) / 2 : (width - 80) / 3;

  const pageAndSearch = (
    <>
      <div className={clsx(classes.firstPartContainer, isAuditLogsHeader && isTwoColumns && classes.noFlex, isBetween && classes.putSecond)}>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%' }}>
          <Select {...props} disabled={props.searchDisabled} />
          {isAuditLogsHeader && isBetween && (
            <Button
              style={{ margin: 0 }}
              type="button"
              variant="outlined"
              className={clsx(
                classes.button,
                isOneColumn && classes.smallSizeButton,
                classes.executeButton,
                classes.buttonNextToShowPerPage,
              )}
              onClick={onAdd2ButtonClick}
              endIcon={<AutorenewIcon />}
            >
              {add2ButtonText}
            </Button>
          )}
        </div>
        {headerMiddleButtonAction && (
          <Button
            type="button"
            variant="outlined"
            className={classes.headerMiddleButtonContainer}
            endIcon={<img src={headerMiddleButtonIcon} />}
            onClick={headerMiddleButtonAction}
          >
            {headerMiddleButtonText}
          </Button>
        )}
        {onChangeIncludeDeletedController && (
          <FormControlLabel
            className={classes.includeDeletedSwitch}
            control={
              <Switch
                checked={includeDeletedController}
                onChange={(event) => onChangeIncludeDeletedController(event)}
                value="includeDeletedController"
              />
            }
            label={t('controllers.includeDeletedControllers')}
            labelPlacement="start"
          />
        )}
      </div>
      <SearchField
        {...props}
        onSearchSubmit={props.handleSearch}
        textFieldWidth={searchWidth}
        searchPlaceholder={props.searchPlaceholder}
        onKeyPress={props.onKeyPress}
        {...(isAuditLogsHeader && { alignWithAuditSmallScreen: !isThreeColumns, alignWithAuditBigScreen: isThreeColumns })}
      />
    </>
  );

  return isAuditLogsHeader ? (
    <div className={clsx([classes.twoRowHeaderContainer])}>
      <div className={clsx([classes.tableHeader, classes.extraPadding, isOneColumn && classes.column])}>
        {/* startTime */}
        <DatePicker
          showPopperArrow={false}
          selected={props.startTime}
          onChange={props.handleStartTimeChange}
          locale={i18n.language}
          dateFormat="d.M.yyyy H:mm"
          timeFormat="H:mm"
          timeIntervals={1}
          shouldCloseOnSelect={false}
          timeInputLabel={`${t('common.time')}:`}
          showTimeInput={true}
          {...(isOneColumn && {
            popperModifiers: {
              offset: {
                enabled: true,
                offset: '15px, 10px',
              },
              preventOverflow: {
                enabled: true,
                escapeWithReference: false,
                boundariesElement: 'viewport',
              },
            },
          })}
          customInput={
            <TextField
              className={classes.textField}
              style={{ width: formInputWidth, marginTop: 20, ...(!isOneColumn && { marginRight: 10 }), maxWidth: 370 }}
              label={t('persons.from')}
              required
            />
          }
        />
        {/* endTime */}
        <DatePicker
          showPopperArrow={false}
          selected={props.endTime}
          {...(props.startTime && {
            minDate: new Date(hackToSubtractOffset(props.startTime)),
          })}
          onChange={props.handleEndTimeChange}
          locale={i18n.language}
          dateFormat="d.M.yyyy H:mm"
          timeFormat="H:mm"
          timeIntervals={1}
          shouldCloseOnSelect={false}
          timeInputLabel={`${t('common.time')}:`}
          showTimeInput={true}
          popperModifiers={{
            offset: {
              enabled: true,
              offset: isOneColumn ? '15px, 10px' : '-20px, 10px',
            },
            preventOverflow: {
              enabled: true,
              escapeWithReference: false,
              boundariesElement: 'viewport',
            },
          }}
          customInput={
            <TextField
              className={classes.textField}
              style={{ width: formInputWidth, marginTop: 20, ...(!isOneColumn && { marginLeft: 10 }), maxWidth: 370 }}
              label={t('persons.to')}
              required
            />
          }
        />
        {isThreeColumns && <div className={classes.emptyColumn} />}
      </div>
      <div className={clsx([classes.tableHeader, classes.extraPadding])}>
        <div className={clsx(classes.pageAndSearchContainer, isBetween && classes.column)}>{pageAndSearch}</div>
        {!isBetween && (
          <div className={classes.executeButtonContainer}>
            <Button
              type="button"
              variant="outlined"
              className={clsx(classes.button, isOneColumn && classes.smallSizeButton, classes.executeButton)}
              onClick={onAdd2ButtonClick}
              endIcon={<AutorenewIcon />}
            >
              {add2ButtonText}
            </Button>
          </div>
        )}
      </div>
    </div>
  ) : isMonitoring ? (
    <div className={clsx([classes.tableHeader, classes.monitoringHeader])}>
      <Button onClick={props.openSitesModal} className={clsx(classes.sitesButton)}>
        {t('widgetInOutCounter_selectSites')}
      </Button>
      {hasAjax && (
        <FormControlLabel
          className={classes.ajaxSwitch}
          control={
            <Switch checked={includeAjax} onChange={(event) => onChangeIncludeAjax(event)} value="includeAjax" />
          }
          label={t('ajax.includeAjax')}
          labelPlacement="start"
        />
      )}
    </div>
  ) : isSelectAll ? (
    <div className={clsx([classes.selectAllHeaderContainer])}>
      <div className={clsx([classes.tableHeader, displayColumn && classes.column])}>{pageAndSearch}</div>
      <div className={classes.checkAll}>
        <Checkbox
          checked={isSelectAllControllersButtonChecked}
          inputProps={{ 'aria-labelledby': 'check-all-sites' }}
          onChange={props.changeSelectAllControllersButton}
        />
        <span className={classes.checkAllLabel}>{t('Global_selectAll')}</span>
      </div>
    </div>
  ) : (
    <div className={clsx([classes.tableHeader, displayColumn && classes.column])}>{pageAndSearch}</div>
  );
};

export default TableHeader;
